import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const schema = z.object({
  email: z.string().email('E-mail inválido'),
});

type FormData = z.infer<typeof schema>;

export function ResetPasswordPage() {
  const [sent, setSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onSubmit = async ({ email }: FormData) => {
    await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/accept-invite`,
    });
    setSent(true);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-secondary p-8">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <img
            src="/identidade-visual/logo/vetor/logo-horizontal.svg"
            alt="Clinance"
            className="h-10 w-auto"
          />
        </div>

        <div className="rounded-2xl border border-border bg-white p-8 shadow-sm">
          {sent ? (
            <div className="py-4 text-center">
              <CheckCircle2 className="mx-auto mb-4 text-green-600" size={48} />
              <h2 className="mb-2 font-brand text-xl font-bold text-foreground">E-mail enviado!</h2>
              <p className="mb-6 text-sm text-muted-foreground">
                Verifique sua caixa de entrada e siga o link para redefinir sua senha.
              </p>
              <Link
                to="/login"
                className="text-sm text-primary transition-colors hover:text-primary/80"
              >
                Voltar ao login
              </Link>
            </div>
          ) : (
            <>
              <h2 className="mb-2 font-brand text-2xl font-bold text-foreground">
                Redefinir senha
              </h2>
              <p className="mb-8 text-sm text-muted-foreground">
                Digite seu e-mail e enviaremos um link para redefinir a senha.
              </p>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
                <div className="space-y-1.5">
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="seu@email.com.br"
                    {...register('email')}
                  />
                  {errors.email && (
                    <p className="text-xs text-destructive">{errors.email.message}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full bg-accent font-semibold text-accent-foreground hover:bg-accent/90"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Enviando…
                    </>
                  ) : (
                    'Enviar link'
                  )}
                </Button>
              </form>

              <div className="mt-6">
                <Link
                  to="/login"
                  className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  <ArrowLeft size={14} />
                  Voltar ao login
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
